import { useId, useState } from 'react';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { PlayInput } from 'Types';
import { requestMidiConnectCheck } from 'Utils/playAlong/midiConnectCheck';
import { selectAccountPlayInput } from 'Utils/playAlong/effectiveGrading';
import {
  PLAY_INPUT_OPTIONS,
  PLAY_INPUT_QUESTION,
  SAVE_FAILED_MESSAGE,
  shownPlayInput,
} from 'Utils/playAlong/playInputAccount';
import { usePlayInputChange } from './usePlayInputChange';

export interface PlayInputPickerProps {
  /** Called once a new answer is saved. */
  onSaved?: (playInput: PlayInput) => void;
}

/**
 * "What do you play on?": the one account answer, with its three choices. The
 * same control in Settings and in every play surface's menu.
 *
 * Choosing "A MIDI keyboard" saves it and then runs the keyboard connect
 * check, so a player with no keyboard connected is asked to connect one rather
 * than switched over silently. The other two choices just save.
 */
export default function PlayInputPicker({ onSaved }: PlayInputPickerProps) {
  const labelId = useId();
  const accountPlayInput = useSelector(selectAccountPlayInput);
  const changePlayInput = usePlayInputChange();
  const [saving, setSaving] = useState<PlayInput | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);
  const shown = shownPlayInput(accountPlayInput);

  const choose = async (next: PlayInput) => {
    if (saving !== null || next === shown) return;
    setSaving(next);
    setSaveFailed(false);
    const saved = await changePlayInput(next, 'settings');
    setSaving(null);
    if (!saved) {
      setSaveFailed(true);
      return;
    }
    if (next === 'midi') requestMidiConnectCheck();
    onSaved?.(next);
  };

  return (
    <Box data-testid="play-input-picker" sx={{ fontFamily: 'Lato' }}>
      <Box
        id={labelId}
        sx={{
          fontSize: '16px',
          letterSpacing: '0.01em',
          color: '#ECECEC',
          marginBottom: '10px',
        }}
      >
        {PLAY_INPUT_QUESTION}
      </Box>
      <Box
        role="radiogroup"
        aria-labelledby={labelId}
        sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}
      >
        {PLAY_INPUT_OPTIONS.map((option) => {
          const selected = option.value === shown;
          return (
            <Box
              key={option.value}
              component="button"
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={saving !== null}
              onClick={() => void choose(option.value)}
              sx={{
                minHeight: '44px',
                padding: '10px 14px',
                borderRadius: '999px',
                border: selected ? '1px solid #7FD7FF' : '1px solid rgba(255,255,255,0.18)',
                backgroundColor: selected ? 'rgba(127, 215, 255, 0.14)' : 'transparent',
                color: selected ? '#ECECEC' : '#9CA3AF',
                fontFamily: 'Lato',
                fontSize: '14px',
                textAlign: 'left',
                cursor: saving !== null ? 'progress' : 'pointer',
                opacity: saving !== null && saving !== option.value ? 0.6 : 1,
                transition: 'all 0.2s ease',
              }}
            >
              {option.title}
            </Box>
          );
        })}
      </Box>
      {saveFailed && (
        <Box role="alert" sx={{ marginTop: '10px', fontSize: '13px', color: '#F3C6B8' }}>
          {SAVE_FAILED_MESSAGE}
        </Box>
      )}
    </Box>
  );
}
